import { useCallback, useMemo } from 'react';
import { Button } from '../components/Button';

const order = ['welcome', 'app', 'notifications', 'survey', 'community'];

const smallMobile = window.matchMedia(
   '(max-width: 450px) or (max-height: 450px)'
).matches;

const Onboarding = ({ progress, onUpdateItem }) => {
   const currentStep = useMemo(
      () =>
         order.find(
            (step) => progress[step] === 'started' || progress[step] === 'new'
         ),
      [progress]
   );

   const handleItemUpdate = useCallback(
      (updateType) => {
         onUpdateItem(currentStep, updateType);
      },
      [currentStep, onUpdateItem]
   );

   let ComponentToRender;

   switch (currentStep) {
      case 'welcome':
         ComponentToRender = Welcome;
         break;
      case 'survey':
         ComponentToRender = Survey;
         break;
      case 'app':
         ComponentToRender = App;
         break;
      case 'community':
         ComponentToRender = Community;
         break;
      case 'notifications':
         ComponentToRender = Notifications;
         break;
   }

   if (ComponentToRender == null) {
      return <List progress={progress} onStepClick={onUpdateItem} />;
   }

   return (
      <ComponentToRender
         status={progress[currentStep]}
         onUpdate={handleItemUpdate}
      />
   );
};

const List = ({ progress, onStepClick }) => {
   const incompleteSteps = order.reduce((incompleteMap, step) => {
      if (progress[step] === 'complete') {
         return incompleteMap;
      }
      return { ...incompleteMap, [step]: true };
   }, null);

   if (incompleteSteps != null) {
      return (
         <article>
            <h2 style={{ whiteSpace: 'nowrap' }}>Getting Started</h2>
            <ul className="content-styled-list">
               {incompleteSteps.app && (
                  <li>
                     <Button
                        className="button-link"
                        onClick={() => onStepClick('app', 'new')}
                     >
                        <span className="emoji">📱</span> "Install" the App
                     </Button>
                  </li>
               )}
               {incompleteSteps.notifications && (
                  <li>
                     <Button
                        className="button-link"
                        onClick={() => onStepClick('notifications', 'new')}
                     >
                        <span className="emoji">🔔</span> Stay Up To Date
                     </Button>
                  </li>
               )}
               {incompleteSteps.survey && (
                  <li>
                     <Button
                        className="button-link"
                        onClick={() => onStepClick('survey', 'new')}
                     >
                        <span className="emoji">🎯</span> Share Your Goals
                     </Button>
                  </li>
               )}
               {incompleteSteps.community && (
                  <li>
                     <Button
                        className="button-link"
                        onClick={() => onStepClick('community', 'new')}
                     >
                        <span className="emoji">👥</span> Join the Community
                     </Button>
                  </li>
               )}
            </ul>
         </article>
      );
   }
   return <Done />;
};

const Welcome = ({ onUpdate }) => (
   <article>
      {/* <h1 style={{ whiteSpace: 'nowrap' }}>🎉 Welcome 🎉</h1> */}
      <h2 style={{ whiteSpace: 'nowrap' }}>Welcome 🎉</h2>
      <p>
         Thank you for joining Ear Instructor! This short guide will help you
         get the most out of your membership.
      </p>
      <div
         style={{
            //  textAlign: 'center',
            marginTop: '2em',
         }}
      >
         <Button className="button-cta" onClick={() => onUpdate('complete')}>
            Let's Get Started!
         </Button>
      </div>
   </article>
);

const Notifications = ({ status, onUpdate }) => (
   <OnboardItemDisplay
      title="Stay Up To Date 🔔"
      content="If you want to be notified when new content is added, you can subscribe
       to receive updates via text or email."
      footnote="You will only receive important updates, never spam!"
      ctaText="Get Updates"
      ctaLink="https://docs.google.com/forms/d/e/1FAIpQLSdQPX2yH18VqI4TDVrNGUAmvCItVVTXj4usET8X1siYCcCFVQ/viewform?usp=dialog"
      status={status}
      onUpdate={onUpdate}
      completeText="Subscribed!"
   />
);

const Community = ({ status, onUpdate }) => (
   <OnboardItemDisplay
      title="Join the Community 👥"
      content="The Ear Instructor Discord is a great place to ask
       questions and meet like-minded producers."
      footnote="You can also request features or report bugs."
      ctaText="Join Discord"
      ctaLink="https://docs.google.com/forms/d/e/1FAIpQLSdQPX2yH18VqI4TDVrNGUAmvCItVVTXj4usET8X1siYCcCFVQ/viewform?usp=dialog"
      status={status}
      onUpdate={onUpdate}
      completeText="Joined!"
   />
);

const Survey = ({ status, onUpdate }) => (
   <OnboardItemDisplay
      title="Share Your Goals 🎯"
      content="Answering a few quick questions will help me make the content that
you want most!"
      footnote="This should take about five minutes."
      ctaText="Take Survey"
      ctaLink="https://docs.google.com/forms/d/e/1FAIpQLSdQPX2yH18VqI4TDVrNGUAmvCItVVTXj4usET8X1siYCcCFVQ/viewform?usp=dialog"
      status={status}
      onUpdate={onUpdate}
      completeText="Survey Complete!"
   />
);

const App = ({ status, onUpdate }) => (
   <OnboardItemDisplay
      title={`"Install" the App 📱`}
      content={
         smallMobile
            ? 'You can fully remove the web browser controls for an app-like experience on mobile.'
            : 'Ear Instructor works on mobile, so you can practice music production anywhere you want!'
      }
      footnote="Just follow a few simple steps. No download required!"
      ctaText="Get the App"
      ctaLink="https://docs.google.com/forms/d/e/1FAIpQLSdQPX2yH18VqI4TDVrNGUAmvCItVVTXj4usET8X1siYCcCFVQ/viewform?usp=dialog"
      status={status}
      onUpdate={onUpdate}
      completeText="Got It!"
   />
);

const Done = () => (
   <article>
      <h2 style={{ whiteSpace: 'nowrap' }}>You're All Set 🙌</h2>
      <p>
         Now you're ready to dive into Ear Instructor and start leveling up your
         music production skills!
      </p>
   </article>
);

const OnboardItemDisplay = ({
   title,
   content,
   footnote,
   ctaText,
   ctaLink,
   status,
   onUpdate,
   completeText,
}) => {
   return (
      <article>
         <h2
            style={{
               whiteSpace: 'nowrap',
            }}
         >
            {title}
         </h2>
         <p>{content}</p>
         <small>{footnote}</small>
         {status === 'started' ? (
            <CancelDoneButtons
               completeButtonText={completeText}
               onCancel={() => onUpdate('new')}
               onComplete={() => onUpdate('complete')}
            />
         ) : (
            <Buttons
               ctaText={ctaText}
               ctaExternalLink={ctaLink}
               onNever={() => onUpdate('complete')}
               onLater={() => onUpdate('deferred')}
               onAction={() => onUpdate('started')}
            />
         )}
      </article>
   );
};

const Buttons = ({ ctaText, ctaExternalLink, onAction, onLater, onNever }) => (
   <div
      style={{
         display: 'flex',
         //  justifyContent: 'center',
         gap: '1em',
         marginTop: '2em',
      }}
   >
      <Button
         className="button-cta"
         as="a"
         target="_blank"
         href={ctaExternalLink}
         onClick={onAction}
      >
         {ctaText}
      </Button>
      <Button onClick={onLater}>Later</Button>
      <Button onClick={onNever}>Never</Button>
   </div>
);

const CancelDoneButtons = ({ completeButtonText, onCancel, onComplete }) => (
   <div
      style={{
         display: 'flex',
         //  justifyContent: 'center',
         gap: '1em',
         marginTop: '2em',
      }}
   >
      <Button className="" onClick={onCancel}>
         Cancel
      </Button>
      <Button className="button-cta" onClick={onComplete}>
         {completeButtonText}
      </Button>
   </div>
);

export { Onboarding };
